import GoogleApi, { loadGoogleApi } from "@bmi-digital/components/google-api";
import Section from "@bmi-digital/components/section";
import { replaceSpaces } from "@bmi-digital/components/utils";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { useLocation } from "@reach/router";
import { graphql } from "gatsby";
import React, { useEffect, useMemo, useReducer, useState } from "react";
import { pushToDataLayer } from "../../utils/google-tag-manager";
import RichText from "../RichText";
import { ServiceTypesPrefixesEnum } from "../Service";
import { useSiteContext } from "../Site";
import { useConfig } from "../../contexts/ConfigProvider";
import {
  createCompanyDetails,
  SearchLocationBlock,
  ServiceLocatorChips,
  ServiceLocatorMap,
  ServiceLocatorResultList
} from "./components";
import {
  DEFAULT_LEVEL_ZOOM,
  PAGE_SIZE,
  PLACE_LEVEL_ZOOM,
  QUERY_CHIP_FILTER_KEY
} from "./constants";
import {
  filterServices,
  getResultDataGtm,
  getRooferTypes,
  getTypesFromServices,
  sortServices
} from "./helpers";
import {
  Body,
  classes,
  Controls,
  MapTabPanel,
  ResultListTabPanel,
  StyledTabs
} from "./styles/styles";
import useMarkers from "./hooks/useMarkers";
import type { Service, ServiceData, ServiceTypeFilter } from "../Service";
import type { RichTextData } from "../RichText";
import type {
  GeocoderResult as GoogleGeocoderResult,
  Google,
  LatLngLiteral as GoogleLatLngLiteral
} from "@bmi-digital/components/google-api";
import type { CompanyDetailProps } from "@bmi-digital/components/company-details";
import type { ServiceTypeData } from "../ServiceType";
import type {
  PayloadServiceEntryType,
  PayloadServiceLocatorSection
} from "../../types/payload";

export type ServiceLocatorSectionData = Pick<
  PayloadServiceLocatorSection,
  | "blockType"
  | "type"
  | "showDefaultResultList"
  | "title"
  | "centre"
  | "zoom"
  | "overlay"
> & {
  body?: RichTextData;
  services: ServiceData[];
};

declare global {
  interface Window {
    google?: Google;
  }
}

const ServiceLocatorSection = ({
  data
}: {
  data: ServiceLocatorSectionData;
}) => {
  const {
    type: sectionType,
    showDefaultResultList,
    title,
    body,
    services,
    zoom: initialMapZoom,
    overlay
  } = data;

  const shouldEnableSearch = sectionType !== "Branch";

  const { getMicroCopy } = useSiteContext();
  const { disableDefaultMapPins } = useConfig();
  const windowLocation = useLocation();

  const params = new URLSearchParams(windowLocation.search);
  const userQueryString = useMemo(
    () => params.get(QUERY_CHIP_FILTER_KEY),
    [params]
  );
  const [uniqueRoofTypeByData, setUniqueRoofTypeByData] = useState<
    ServiceTypeData[]
  >([]);
  const [isUserAction, setUserAction] = useState(false);
  const [page, setPage] = useState(1);
  const [pagedFilteredRoofers, setPagedFilteredRoofers] = useState<Service[]>(
    []
  );
  const [showResultList, setShowResultList] = useState(showDefaultResultList);
  const [googleApi, setGoogleApi] = useState<Google>(null);
  const [selectedRoofer, setSelectedRoofer] = useState<Service | null>(null);
  const [centre, setCentre] = useState<GoogleLatLngLiteral | undefined>();
  const [zoom, setZoom] = useState<number>(
    initialMapZoom || DEFAULT_LEVEL_ZOOM
  );
  const [activeSearchString, setActiveSearchString] = useState<string>("");
  const [userPosition, setUserPosition] = useState<
    | undefined
    | {
        location: GoogleLatLngLiteral;
      }
  >();
  const [activeLocation, setActiveLocation] = useState<
    GoogleGeocoderResult | undefined
  >(undefined);
  const [isLocationSearchFailed, setIsLocationSearchFailed] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(
    Boolean(overlay?.title)
  );

  const nameSearchLabelKey = getMicroCopy(
    `findARoofer.${
      sectionType === "Merchant"
        ? "merchantNameSearchLabel"
        : "companyFieldLabel"
    }`
  );

  const serviceTypesByEntityItems: ServiceTypeData[] = services
    ? getTypesFromServices(services)
    : [];

  const activeFilterReducer = (
    state: ServiceTypeFilter,
    filter: {
      serviceType: ServiceTypeData;
      state?: boolean;
    }
  ) => {
    const result = {
      ...state,
      [filter.serviceType.name]: filter.state
        ? filter.state
        : !state[filter.serviceType.name]
    };
    return result;
  };

  const initialActiveFilters = serviceTypesByEntityItems.reduce(
    (carry, key: ServiceTypeData) => ({ ...carry, [key.name]: false }),
    {}
  ) as ServiceTypeFilter;

  const [activeFilters, updateActiveFilters] = useReducer(
    activeFilterReducer,
    initialActiveFilters
  );

  const filteredRoofers = useMemo<Service[]>(
    () =>
      isLocationSearchFailed
        ? []
        : services
            .reduce(
              filterServices(centre, activeFilters, activeSearchString),
              []
            )
            .sort(sortServices(centre)),
    [
      activeFilters,
      activeSearchString,
      centre,
      isLocationSearchFailed,
      services
    ]
  );

  const markers = useMarkers({
    activeSearchString,
    activeLocation,
    selectedRoofer,
    pagedFilteredRoofers,
    showResultList: Boolean(showResultList),
    filteredRoofers,
    type: sectionType
  });

  const handlePageChange = (
    _: React.ChangeEvent<HTMLElement>,
    pageNumber: number
  ) => {
    setPagedFilteredRoofers(() => {
      const start = (pageNumber - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      return filteredRoofers.slice(start, end);
    });
    setSelectedRoofer(null);
    setPage(pageNumber);
  };

  useEffect(() => {
    setPagedFilteredRoofers(filteredRoofers.slice(0, PAGE_SIZE));
    setPage(1);
  }, [filteredRoofers]);

  const pageCount = Math.ceil(filteredRoofers.length / PAGE_SIZE);

  const handleServiceClick = (service: Service, isMarker = false) => {
    setSelectedRoofer(service);
    setIsOverlayVisible(false);
    if (isMarker) {
      pushToDataLayer(getResultDataGtm(service, isMarker));
    }
  };

  const changeZoomLevel = async (newZoom: number) => {
    /**
     * need to trigger zoom change event two times,
     * as due to an issue in the common components,
     * the first zoom change will be ignored
     *
     * This will be refactored in the scope of DXB-11446
     */
    setZoom(newZoom + 1);
    await new Promise((resolve) =>
      setTimeout(() => {
        setZoom(newZoom);
        resolve(true);
      }, 0)
    );
  };

  const handlePlaceChange = (
    location?: GoogleGeocoderResult,
    isUserSearch = false
  ) => {
    setActiveLocation(location);
    setSelectedRoofer(null);
    if (location) {
      setIsLocationSearchFailed(false);
    } else if (isUserSearch) {
      setIsLocationSearchFailed(true);
    }
    if (location || isUserSearch) {
      setIsOverlayVisible(false);
      setShowResultList(true);
    }

    const defaultZoom = initialMapZoom || DEFAULT_LEVEL_ZOOM;
    const newZoom =
      disableDefaultMapPins && sectionType === "Merchant"
        ? defaultZoom
        : location
          ? PLACE_LEVEL_ZOOM
          : defaultZoom;

    changeZoomLevel(newZoom);
    setCentre(
      location && {
        lat: location.geometry.location.lat(),
        lng: location.geometry.location.lng()
      }
    );
  };

  const clearRooferAndResetMap = async () => {
    const resetToDefaultState =
      disableDefaultMapPins && sectionType === "Merchant";
    const defaultZoom = initialMapZoom || DEFAULT_LEVEL_ZOOM;

    setSelectedRoofer(null);
    if (resetToDefaultState) {
      await changeZoomLevel(defaultZoom);
      return;
    }

    setZoom(centre ? PLACE_LEVEL_ZOOM : defaultZoom);
    setCentre(centre);
  };

  const getCompanyDetails = (service: Service): CompanyDetailProps => {
    return createCompanyDetails({ sectionType, service, getMicroCopy, centre });
  };

  const getMicroCopyPrefix = (serviceType: PayloadServiceEntryType) => {
    // eslint-disable-next-line security/detect-object-injection
    return ServiceTypesPrefixesEnum[serviceType];
  };

  const microCopyPrefix = getMicroCopyPrefix(sectionType);

  const handleAutocompleteOnChange = async (_, inputValue) => {
    const resetToDefaultState =
      disableDefaultMapPins && sectionType === "Merchant";

    setSelectedRoofer(null);
    setIsOverlayVisible(false);
    setShowResultList(true);
    setActiveSearchString(inputValue || "");

    if (resetToDefaultState) {
      await changeZoomLevel(initialMapZoom || DEFAULT_LEVEL_ZOOM);
    }

    if (inputValue) {
      pushToDataLayer({
        event: "dxb.button_click",
        id: "filter-service-locator",
        label: nameSearchLabelKey,
        action: inputValue
      });
    }
  };

  const onChipClick = (serviceType: ServiceTypeData) => {
    setUserAction(true);
    setShowResultList(true);
    updateActiveFilters({ serviceType: serviceType });
    setIsOverlayVisible(false);
    setSelectedRoofer(null);
  };

  const initialise = async () => {
    await loadGoogleApi(process.env.GATSBY_GOOGLE_API_KEY, [
      "places",
      "geometry"
    ]);
    setGoogleApi(typeof window?.google !== "undefined" ? window.google : null);
  };

  const getPosition = () => {
    return ({ coords }) => {
      setUserPosition({
        location: {
          lat: coords.latitude,
          lng: coords.longitude
        }
      });
    };
  };

  useEffect(() => {
    if (!services) {
      return;
    }

    const uniqueRooferTypes: ServiceTypeData[] = getTypesFromServices(services);
    setUniqueRoofTypeByData(uniqueRooferTypes);

    if (!userQueryString) {
      return;
    }

    const allQueries = userQueryString.split(",");

    const matchingRooferTypes = getRooferTypes(uniqueRooferTypes, allQueries);

    // there were no matching queries in querystring
    // hence remove all querystring from user and make the url '/find-a-roofer/' again
    if (matchingRooferTypes.length === 0) {
      history.replaceState(null, "", windowLocation.pathname);
    } else {
      // show result list panel on page load if selected chips exist
      matchingRooferTypes.forEach((serviceType: ServiceTypeData) => {
        updateActiveFilters({ serviceType: serviceType, state: true });
      });
      setShowResultList(true);
    }
  }, [services, userQueryString]);

  useEffect(() => {
    if (!userQueryString) {
      const isEveryChipSelected = Object.keys(activeFilters).every(
        // eslint-disable-next-line security/detect-object-injection
        (key) => activeFilters[key] === true
      );
      if (isEveryChipSelected) {
        return;
      }
    }

    const filteredChips: string[] = Object.keys(activeFilters).filter(
      // eslint-disable-next-line security/detect-object-injection
      (key) => activeFilters[key]
    );

    if (filteredChips.length > 0) {
      const queryParams = new URLSearchParams(windowLocation.search);
      queryParams.set(QUERY_CHIP_FILTER_KEY, filteredChips.join(","));
      history.replaceState(null, "", "?" + queryParams.toString());
    }
    if (filteredChips.length === 0 && isUserAction) {
      // Remove the query if there are no selected chips
      // otherwise url will look like `/?chip=`
      history.replaceState(null, "", windowLocation.pathname);
    }
  }, [activeFilters]);

  useEffect(() => {
    initialise();
  }, []);

  return (
    <Section
      backgroundColor="white"
      data-testid={`service-locator-section-${replaceSpaces(title)}`}
    >
      <GoogleApi.Provider value={googleApi}>
        {title && <Section.Title>{title}</Section.Title>}
        {body && body.root.children.length ? (
          <Body>
            <RichText document={body} />
          </Body>
        ) : undefined}
        <Controls container spacing={3} alignItems="flex-end">
          {shouldEnableSearch && (
            <SearchLocationBlock
              autocompleteLabel={nameSearchLabelKey}
              options={filteredRoofers.map(({ name }) => name)}
              handleAutocompleteOnChange={handleAutocompleteOnChange}
              handlePlaceChange={handlePlaceChange}
              getPosition={getPosition}
              userPosition={userPosition}
            />
          )}
          {uniqueRoofTypeByData.length > 1 && (
            <ServiceLocatorChips
              uniqueRoofTypeByData={uniqueRoofTypeByData}
              onChipClick={onChipClick}
              activeFilters={activeFilters}
              microCopyPrefix={microCopyPrefix}
            />
          )}
        </Controls>
        <StyledTabs
          initialValue="list"
          color="secondary"
          visibleUntil="lg"
          variant="fullWidth"
        >
          {showResultList && (
            <ResultListTabPanel
              className={classes.tabPanel}
              heading={getMicroCopy(microCopy.FIND_A_ROOFER_LIST_LABEL)}
              index="list"
            >
              <ServiceLocatorResultList
                roofersList={pagedFilteredRoofers}
                onListItemClick={handleServiceClick}
                onCloseCard={clearRooferAndResetMap}
                onPageChange={handlePageChange}
                page={page}
                pageCount={pageCount}
                getCompanyDetails={getCompanyDetails}
                selectedRoofer={selectedRoofer}
                shouldListCertification
              />
            </ResultListTabPanel>
          )}
          <MapTabPanel
            className={classes.tabPanel}
            heading={getMicroCopy(microCopy.FIND_A_ROOFER_MAP_LABEL)}
            index="map"
          >
            <ServiceLocatorMap
              selectedRoofer={selectedRoofer}
              clearRooferAndResetMap={clearRooferAndResetMap}
              handleCloseCard={() => setSelectedRoofer(null)}
              initialMapCentre={data.centre}
              centre={centre}
              markers={markers}
              handleMarkerClick={(service) => handleServiceClick(service, true)}
              zoom={zoom}
              getCompanyDetails={getCompanyDetails}
              onDismissOverlay={() => setIsOverlayVisible(false)}
              overlay={isOverlayVisible ? overlay : null}
            />
          </MapTabPanel>
        </StyledTabs>
      </GoogleApi.Provider>
    </Section>
  );
};

export default ServiceLocatorSection;

export const query = graphql`
  fragment ServiceLocatorSectionFragment on PayloadServiceLocatorSection {
    blockType
    title
    type
    showDefaultResultList
    body {
      ...RichTextFragment
    }
    services {
      ... on PayloadService {
        ...ServiceFragment
      }
    }
    centre
    zoom
    overlay {
      title
      description
    }
  }
`;
